const BOTTOM_TOLERANCE_PX = 250;
const MESSAGE_ELEMENT_PREFIX = "customer-pc-msg-";

const hasValue = value => value !== undefined && value !== null && value !== "";

const toStableSort = value => {
  const sort = Number(value);
  return Number.isFinite(sort) && sort > 0 ? sort : undefined;
};

const hasStableCoordinateConflict = (left, right) => {
  const leftSort = toStableSort(left && left.sort);
  const rightSort = toStableSort(right && right.sort);
  if (
    leftSort === undefined ||
    rightSort === undefined ||
    !hasValue(left && left.channelId) ||
    !hasValue(right && right.channelId)
  ) {
    return false;
  }

  return (
    String(left.channelId) !== String(right.channelId) || leftSort !== rightSort
  );
};

/**
 * Resolve one old rendered message in the next list without guessing through
 * identity collisions. This intentionally mirrors reconnect merge identity:
 * msgId first, then channelId+positive sort when the old record has no msgId.
 */
export const findUniqueReconcileMessageIndex = (messages, target) => {
  if (!target || !Array.isArray(messages)) return -1;

  const referenceMatches = messages
    .map((message, index) => ({ message, index }))
    .filter(item => item.message === target);
  if (referenceMatches.length > 0) {
    return referenceMatches.length === 1 ? referenceMatches[0].index : -1;
  }

  if (hasValue(target.msgId)) {
    const matches = messages
      .map((message, index) => ({ message, index }))
      .filter(
        item =>
          hasValue(item.message && item.message.msgId) &&
          String(item.message.msgId) === String(target.msgId) &&
          !hasStableCoordinateConflict(item.message, target)
      );
    return matches.length === 1 ? matches[0].index : -1;
  }

  const targetSort = toStableSort(target.sort);
  if (!hasValue(target.channelId) || targetSort === undefined) return -1;

  const matches = messages
    .map((message, index) => ({ message, index }))
    .filter(
      item =>
        hasValue(item.message && item.message.channelId) &&
        String(item.message.channelId) === String(target.channelId) &&
        toStableSort(item.message.sort) === targetSort
    );
  return matches.length === 1 ? matches[0].index : -1;
};

const getMessageElement = (messageBox, index) =>
  messageBox.querySelector(`#${MESSAGE_ELEMENT_PREFIX}${index}`);

const toNonNegativeNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const getDocumentMetrics = documentObject => {
  const documentElement = documentObject && documentObject.documentElement;
  const body = documentObject && documentObject.body;
  const windowObject = documentObject && documentObject.defaultView;
  const scrollElement =
    (documentObject && documentObject.scrollingElement) ||
    documentElement ||
    body;
  const scrollTop = Math.max(
    toNonNegativeNumber(windowObject && windowObject.pageYOffset),
    toNonNegativeNumber(scrollElement && scrollElement.scrollTop),
    toNonNegativeNumber(documentElement && documentElement.scrollTop),
    toNonNegativeNumber(body && body.scrollTop)
  );
  const scrollHeight = Math.max(
    toNonNegativeNumber(scrollElement && scrollElement.scrollHeight),
    toNonNegativeNumber(documentElement && documentElement.scrollHeight),
    toNonNegativeNumber(documentElement && documentElement.offsetHeight),
    toNonNegativeNumber(body && body.scrollHeight),
    toNonNegativeNumber(body && body.offsetHeight)
  );
  const clientHeight =
    [
      windowObject && windowObject.innerHeight,
      documentElement && documentElement.clientHeight,
      body && body.clientHeight
    ]
      .map(toNonNegativeNumber)
      .find(value => value > 0) || 0;

  return { scrollTop, scrollHeight, clientHeight };
};

const setDocumentScrollTop = (documentObject, value) => {
  const top = Math.max(0, Number(value) || 0);
  const documentElement = documentObject && documentObject.documentElement;
  const body = documentObject && documentObject.body;
  const scrollElement =
    (documentObject && documentObject.scrollingElement) ||
    documentElement ||
    body;
  const windowObject = documentObject && documentObject.defaultView;

  if (scrollElement) scrollElement.scrollTop = top;
  if (documentElement) documentElement.scrollTop = top;
  if (body) body.scrollTop = top;
  if (windowObject && typeof windowObject.scrollTo === "function") {
    try {
      windowObject.scrollTo(0, top);
    } catch (error) {
      // Direct scroll-element assignment above is the cross-browser fallback.
    }
  }
};

/**
 * PC scrolls the message box, while H5 lays the box out at full height and
 * scrolls the document. Keep one adapter so the reconciliation watcher applies
 * the same top/middle/bottom invariants to the element that actually moves.
 */
const resolveScrollViewport = messageBox => {
  const boxMetrics = {
    scrollTop: toNonNegativeNumber(messageBox && messageBox.scrollTop),
    scrollHeight: toNonNegativeNumber(messageBox && messageBox.scrollHeight),
    clientHeight: toNonNegativeNumber(messageBox && messageBox.clientHeight)
  };
  if (boxMetrics.scrollHeight > boxMetrics.clientHeight + 1) {
    return {
      kind: "element",
      owner: messageBox,
      read: () => ({
        scrollTop: toNonNegativeNumber(messageBox.scrollTop),
        scrollHeight: toNonNegativeNumber(messageBox.scrollHeight),
        clientHeight: toNonNegativeNumber(messageBox.clientHeight)
      }),
      rect: () => messageBox.getBoundingClientRect(),
      setScrollTop: value => {
        messageBox.scrollTop = Math.max(0, Number(value) || 0);
      }
    };
  }

  const documentObject =
    (messageBox && messageBox.ownerDocument) ||
    (typeof document === "undefined" ? undefined : document);
  const documentMetrics = getDocumentMetrics(documentObject);
  if (documentMetrics.scrollHeight > documentMetrics.clientHeight + 1) {
    return {
      kind: "document",
      owner: documentObject,
      read: () => getDocumentMetrics(documentObject),
      rect: () => {
        const metrics = getDocumentMetrics(documentObject);
        return { top: 0, bottom: metrics.clientHeight };
      },
      setScrollTop: value => setDocumentScrollTop(documentObject, value)
    };
  }

  return {
    kind: "element",
    owner: messageBox,
    read: () => ({
      scrollTop: toNonNegativeNumber(messageBox && messageBox.scrollTop),
      scrollHeight: toNonNegativeNumber(messageBox && messageBox.scrollHeight),
      clientHeight: toNonNegativeNumber(messageBox && messageBox.clientHeight)
    }),
    rect: () => messageBox.getBoundingClientRect(),
    setScrollTop: value => {
      messageBox.scrollTop = Math.max(0, Number(value) || 0);
    }
  };
};

const isSameScrollViewport = (left, right) =>
  left && right && left.kind === right.kind && left.owner === right.owner;

const getVisibleMessageElements = (
  messageBox,
  messageCount,
  scrollViewport
) => {
  const viewportRect = scrollViewport.rect();
  const visible = [];

  for (let index = 0; index < messageCount; index += 1) {
    const element = getMessageElement(messageBox, index);
    if (!element) continue;
    const rect = element.getBoundingClientRect();
    if (rect.bottom > viewportRect.top && rect.top < viewportRect.bottom) {
      visible.push({ element, index });
    }
  }

  return visible;
};

const sameScope = (state, metadata) =>
  state &&
  metadata &&
  String(state.userId) === String(metadata.customerId) &&
  String(state.channelId) === String(metadata.channelId) &&
  Number(state.ws && state.ws.historyGeneration) ===
    Number(metadata.generation) &&
  state.hasHistoryLoad !== true;

const canRestoreViewport = (vm, messageBox, expectedRevision) => {
  const currentMetadata = vm.historyReconcileViewport;
  return Boolean(
    !vm._isDestroyed &&
      !vm._isBeingDestroyed &&
      vm.$refs &&
      vm.$refs.box === messageBox &&
      Number(currentMetadata && currentMetadata.revision) ===
        expectedRevision &&
      sameScope(vm.$store && vm.$store.state, currentMetadata)
  );
};

export default {
  data() {
    return {
      handledHistoryReconcileRevision: 0
    };
  },
  computed: {
    historyReconcileViewport() {
      const chatState = this.$store && this.$store.state.chat;
      return (chatState && chatState.historyReconcileViewport) || {};
    }
  },
  created() {
    this.handledHistoryReconcileRevision =
      Number(this.historyReconcileViewport.revision) || 0;
  },
  methods: {
    preserveHistoryReconcileViewport(nextMessages, previousMessages) {
      const metadata = this.historyReconcileViewport;
      const revision = Number(metadata && metadata.revision) || 0;
      if (revision === this.handledHistoryReconcileRevision) return false;
      this.handledHistoryReconcileRevision = revision;

      const state = this.$store && this.$store.state;
      const messageBox = this.$refs && this.$refs.box;
      if (
        !sameScope(state, metadata) ||
        !messageBox ||
        !Array.isArray(nextMessages) ||
        !Array.isArray(previousMessages)
      ) {
        return true;
      }

      const scrollViewport = resolveScrollViewport(messageBox);
      const scrollMetrics = scrollViewport.read();
      const distanceFromBottom =
        scrollMetrics.scrollHeight -
        scrollMetrics.scrollTop -
        scrollMetrics.clientHeight;
      const hasOverflow =
        scrollMetrics.scrollHeight > scrollMetrics.clientHeight + 1;
      // At the actual top, keep scrollTop=0 so newly recovered older records
      // are visible. This takes precedence over the near-bottom tolerance on a
      // short, scrollable conversation.
      if (hasOverflow && scrollMetrics.scrollTop <= 1) return true;

      if (!hasOverflow || distanceFromBottom <= BOTTOM_TOLERANCE_PX) {
        const expectedRevision = revision;
        this.$nextTick(() => {
          if (!canRestoreViewport(this, messageBox, expectedRevision)) return;
          const currentScrollViewport = resolveScrollViewport(messageBox);
          const currentMetrics = currentScrollViewport.read();
          currentScrollViewport.setScrollTop(
            currentMetrics.scrollHeight - currentMetrics.clientHeight
          );
          this.showBottom = false;
        });
        return true;
      }

      const anchor = getVisibleMessageElements(
        messageBox,
        previousMessages.length,
        scrollViewport
      ).find(item => {
        item.nextIndex = findUniqueReconcileMessageIndex(
          nextMessages,
          previousMessages[item.index]
        );
        return item.nextIndex >= 0;
      });
      if (!anchor) return true;

      const previousTop = anchor.element.getBoundingClientRect().top;
      const previousScrollTop = scrollMetrics.scrollTop;
      const expectedRevision = revision;
      this.$nextTick(() => {
        if (!canRestoreViewport(this, messageBox, expectedRevision)) return;
        const currentScrollViewport = resolveScrollViewport(messageBox);
        if (!isSameScrollViewport(scrollViewport, currentScrollViewport)) {
          return;
        }

        const nextElement = getMessageElement(messageBox, anchor.nextIndex);
        if (!nextElement) return;
        const offset = nextElement.getBoundingClientRect().top - previousTop;
        if (Number.isFinite(offset) && offset !== 0) {
          currentScrollViewport.setScrollTop(previousScrollTop + offset);
        }
      });
      return true;
    }
  }
};
